import React, { useState } from 'react';
import { 
  User, 
  Briefcase, 
  ShoppingBag, 
  Wrench, 
  Calendar, 
  Box, 
  ChevronRight, 
  ArrowLeft,
  CheckCircle,
  MapPin,
  GitBranch,
  Store,
  Home,
  Globe,
  Instagram,
  ShoppingCart,
  Camera,
  ExternalLink,
  FileText,
  AlertTriangle,
  Info
} from 'lucide-react';

// --- Configuration Data ---

const PERSONAS = [
  { id: 'individual', title: 'Individual / Solopreneur', icon: User, desc: 'I am a freelancer or digital creator.' },
  { id: 'homepreneur', title: 'Home Business / First-Timer', icon: Home, desc: 'I am a housewife, student, or selling from home.' },
  { id: 'business', title: 'Registered Business', icon: Briefcase, desc: 'I have a GSTIN and an established brand.' }
];

const INTENTS = [
  { id: 'products', title: 'Physical Products', icon: ShoppingBag, desc: 'Clothes, Food, Electronics' },
  { id: 'services', title: 'Services', icon: Wrench, desc: 'Consulting, Cleaning, Photography' },
  { id: 'rentals', title: 'Rentals', icon: Box, desc: 'Costumes, Equipment, Property' },
  { id: 'events', title: 'Events / Tickets', icon: Calendar, desc: 'Workshops, Classes, Webinars' }
];

const CONTEXTS = [
  { id: 'fresh', title: 'My First Store', icon: Store, desc: 'I am setting up my first business on Aniyor.' },
  { id: 'vertical', title: 'New Business Vertical', icon: Briefcase, desc: 'I already have a store on Aniyor, this is for a different category.' },
  { id: 'branch', title: 'New Location / Branch', icon: MapPin, desc: 'I am expanding my existing business to a new pincode.' },
  { id: 'institutional', title: 'Institutional Branch', icon: GitBranch, desc: 'I am setting up a center for a Trust/NGO/Society.' }
];

const ONLINE_HISTORY = [
  { id: 'none', title: 'No, I am new to online selling', icon: User },
  { id: 'website', title: 'Yes, I have my own website (Shopify/Woo)', icon: Globe },
  { id: 'marketplace', title: 'Yes, on Amazon/Flipkart/Meesho', icon: ShoppingCart },
  { id: 'social', title: 'Yes, via Instagram/WhatsApp', icon: Instagram }
];

// --- Components ---

const Card = ({ selected, onClick, title, desc, icon: Icon, compact = false }) => (
  <div 
    onClick={onClick}
    className={`cursor-pointer border-2 rounded-xl transition-all duration-200 flex flex-col gap-3 h-full relative ${
      compact ? 'p-3' : 'p-4'
    } ${
      selected 
        ? 'border-blue-600 bg-blue-50 shadow-md ring-2 ring-blue-100' 
        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${selected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
        <Icon size={20} />
      </div>
      {compact && <h3 className={`font-bold text-sm ${selected ? 'text-blue-900' : 'text-gray-800'}`}>{title}</h3>}
    </div>
    {!compact && (
      <div>
        <h3 className={`font-bold text-base ${selected ? 'text-blue-900' : 'text-gray-800'}`}>{title}</h3>
        {desc && <p className="text-xs text-gray-500 mt-1 leading-snug">{desc}</p>}
      </div>
    )}
    {selected && <div className="absolute top-4 right-4 text-blue-600"><CheckCircle size={18} /></div>}
  </div>
);

// --- Wizard Steps ---

const StepPersona = ({ data, updateData, onNext }) => (
  <div className="space-y-6 animate-in slide-in-from-right-8 duration-500">
    <div className="text-center mb-6">
      <h2 className="text-2xl font-bold text-gray-900">Let&apos;s get started</h2>
      <p className="text-gray-500 mt-2 text-sm">Tell us about yourself so we can set up the right tools for you.</p>
    </div>
    
    <div className="grid grid-cols-1 gap-3">
      {PERSONAS.map(p => (
        <Card 
          key={p.id}
          title={p.title} 
          desc={p.desc} 
          icon={p.icon} 
          selected={data.persona === p.id}
          onClick={() => updateData({ persona: p.id })}
        />
      ))}
    </div>

    <button 
      disabled={!data.persona}
      onClick={onNext}
      className="w-full mt-6 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
    >
      Next <ChevronRight size={18} />
    </button>
  </div>
);

const StepIntent = ({ data, updateData, onNext, onBack }) => {
  const toggleIntent = (id) => {
    const current = data.intents || [];
    const updated = current.includes(id) 
      ? current.filter(i => i !== id) 
      : [...current, id];
    updateData({ intents: updated });
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-right-8 duration-500">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-600"><ArrowLeft size={20}/></button>
        <span className="text-sm font-bold text-blue-600 uppercase tracking-wide">Step 2 of 4</span>
      </div>
      
      <div>
        <h2 className="text-2xl font-bold text-gray-900">What will you list?</h2>
        <p className="text-gray-500 mt-2 text-sm">Select all that apply. We support mixed business models!</p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {INTENTS.map(intent => (
          <div key={intent.id} className="relative">
             <Card 
              title={intent.title} 
              desc={intent.desc} 
              icon={intent.icon} 
              selected={data.intents?.includes(intent.id)}
              onClick={() => toggleIntent(intent.id)}
            />
          </div>
        ))}
      </div>

      <button 
        disabled={!data.intents || data.intents.length === 0}
        onClick={onNext}
        className="w-full mt-6 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
      >
        Continue <ChevronRight size={18} />
      </button>
    </div>
  );
};

const StepContext = ({ data, updateData, onNext, onBack }) => (
  <div className="space-y-6 animate-in slide-in-from-right-8 duration-500">
    <div className="flex items-center gap-2 mb-2">
      <button onClick={onBack} className="text-gray-400 hover:text-gray-600"><ArrowLeft size={20}/></button>
      <span className="text-sm font-bold text-blue-600 uppercase tracking-wide">Step 3 of 4</span>
    </div>
    
    <div>
      <h2 className="text-2xl font-bold text-gray-900">Business Structure</h2>
      <p className="text-gray-500 mt-2 text-sm">Are you starting fresh or expanding an existing business?</p>
    </div>
    
    <div className="grid grid-cols-1 gap-3">
      {CONTEXTS.map(ctx => (
        <Card 
          key={ctx.id}
          title={ctx.title} 
          desc={ctx.desc} 
          icon={ctx.icon} 
          selected={data.context === ctx.id}
          onClick={() => updateData({ context: ctx.id })}
        />
      ))}
    </div>

    <button 
      disabled={!data.context}
      onClick={onNext}
      className="w-full mt-6 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
    >
      Continue <ChevronRight size={18} />
    </button>
  </div>
);

// NEW STEP: Online History Check
const StepOnlineHistory = ({ data, updateData, onNext, onBack }) => {
  const isNewSeller = data.online_history === 'none';
  const sellsProducts = data.intents?.includes('products');
  const sellsServices = data.intents?.some(i => ['services', 'events'].includes(i));
  
  // Show Product Photo question if they sell products and are new
  const showPhotoQuestion = isNewSeller && sellsProducts;
  
  // Show Service Menu question if they sell services/events AND are new AND NOT selling products (to avoid clutter)
  const showServiceQuestion = isNewSeller && sellsServices && !sellsProducts;

  return (
    <div className="space-y-6 animate-in slide-in-from-right-8 duration-500">
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-600"><ArrowLeft size={20}/></button>
        <span className="text-sm font-bold text-blue-600 uppercase tracking-wide">Step 4 of 4</span>
      </div>
      
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Do you already sell online?</h2>
        <p className="text-gray-500 mt-2 text-sm">We can help you import your content later.</p>
      </div>
      
      <div className="grid grid-cols-1 gap-3">
        {ONLINE_HISTORY.map(h => (
          <div key={h.id} className="relative">
            <Card 
              title={h.title} 
              icon={h.icon} 
              compact={true}
              selected={data.online_history === h.id}
              onClick={() => updateData({ online_history: h.id })}
            />
          </div>
        ))}
      </div>

      {/* Child Question: Photography Check */}
      {showPhotoQuestion && (
        <div className="mt-4 bg-indigo-50 border border-indigo-100 p-4 rounded-xl animate-in slide-in-from-bottom-2 fade-in duration-300">
            <h3 className="font-bold text-indigo-900 text-sm flex items-center gap-2">
                <Camera size={18} /> Product Photography Check
            </h3>
            <p className="text-xs text-indigo-700 mt-1 mb-3">
                Do you have professional photographs of your products? High-quality images increase sales by 3x.
            </p>
            
            <div className="flex gap-3">
                <button 
                    onClick={() => updateData({ has_photos: true })}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold border transition-colors ${data.has_photos === true ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-indigo-600 border-indigo-200 hover:border-indigo-400'}`}
                >
                    Yes, I have photos
                </button>
                <button 
                    onClick={() => updateData({ has_photos: false })}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold border transition-colors ${data.has_photos === false ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-indigo-600 border-indigo-200 hover:border-indigo-400'}`}
                >
                    No, I need help
                </button>
            </div>

            {/* Upsell Link if No */}
            {data.has_photos === false && (
                <div className="mt-3 pt-3 border-t border-indigo-200">
                    <p className="text-xs text-indigo-800 mb-2 font-medium">We offer professional photography services!</p>
                    <a 
                        href="https://www.aniyor.net/collections/photo-videography" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-2.5 bg-white border border-indigo-200 text-indigo-700 rounded-lg text-xs font-bold hover:bg-indigo-50 transition-colors shadow-sm"
                    >
                        Book a Photoshoot <ExternalLink size={14} />
                    </a>
                </div>
            )}
        </div>
      )}

      {/* Child Question: Service Menu Check */}
      {showServiceQuestion && (
        <div className="mt-4 bg-purple-50 border border-purple-100 p-4 rounded-xl animate-in slide-in-from-bottom-2 fade-in duration-300">
            <h3 className="font-bold text-purple-900 text-sm flex items-center gap-2">
                <FileText size={18} /> Service Rate Card
            </h3>
            <p className="text-xs text-purple-700 mt-1 mb-3">
                Do you have a detailed Rate Card or Portfolio PDF to upload?
            </p>
            
            <div className="flex gap-3">
                <button 
                    onClick={() => updateData({ has_menu: true })}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold border transition-colors ${data.has_menu === true ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-purple-600 border-purple-200 hover:border-purple-400'}`}
                >
                    Yes, Ready
                </button>
                <button 
                    onClick={() => updateData({ has_menu: false })}
                    className={`flex-1 py-2 px-3 rounded-lg text-xs font-bold border transition-colors ${data.has_menu === false ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-purple-600 border-purple-200 hover:border-purple-400'}`}
                >
                    Not yet
                </button>
            </div>
        </div>
      )}

      <button 
        disabled={!data.online_history || (showPhotoQuestion && data.has_photos === null) || (showServiceQuestion && data.has_menu === null)}
        onClick={onNext}
        className="w-full mt-6 bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        Complete Setup <ChevronRight size={18} />
      </button>
    </div>
  );
};

const StepTransition = ({ data, onComplete }) => {
  // Use data to customize the message
  const sellsGoods = data.intents?.includes('products');
  const sellsServices = data.intents?.some(i => ['services', 'rentals', 'events'].includes(i));
  const isMixedModel = sellsGoods && sellsServices;
  const isNewbie = data.online_history === 'none';
  const isIndividualOrHomepreneur = data.persona === 'individual' || data.persona === 'homepreneur';
  const isBusiness = data.persona === 'business';

  // State for GST exemption checkbox
  const [gstExempted, setGstExempted] = React.useState(false);

  const handleComplete = () => {
    // Pass the GST exemption status along with other data
    onComplete({ ...data, gst_exempted: gstExempted });
  };

  return (
    <div className="space-y-4 animate-in slide-in-from-right-8 duration-500 text-center py-4">
      <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
        <CheckCircle size={32} />
      </div>
      
      <h2 className="text-2xl font-bold text-gray-900">Profile Created!</h2>
      <p className="text-gray-500 mt-2 max-w-xs mx-auto">
        Thanks! We have customized the registration form for you.
      </p>

      {/* Tax Alert - Different messaging based on persona */}
      {isBusiness ? (
        <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg flex gap-3 text-amber-900 text-sm max-w-sm mx-auto text-left">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>
            <span className="font-bold">GST Requirement:</span>
            <p className="mt-1 text-xs opacity-90 leading-relaxed">
              {isMixedModel ? "To sell both Products and Services, your GST Registration must include relevant HSN (for Goods) and SAC (for Services) codes." : 
               sellsGoods ? "You need HSN codes for your products." : 
               "You need SAC codes for your services."}
            </p>
          </div>
        </div>
      ) : (
        /* Individual / Homepreneur - Show Enrollment ID suggestion */
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-left max-w-sm mx-auto">
          <div className="flex items-start gap-2 mb-3">
            <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-bold text-blue-900">Don&apos;t have GSTIN yet?</span>
              <p className="mt-1 text-xs text-blue-700 leading-relaxed">
                No worries! You can register using an <strong>Enrollment ID (EID)</strong> from the GST portal. 
                This allows you to start selling within your state while your GST application is pending.
              </p>
            </div>
          </div>
          <a 
            href="https://reg.gst.gov.in/registration/generateuid" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
          >
            Apply for Enrollment ID <ExternalLink size={12} />
          </a>
        </div>
      )}

      {/* GST Exemption Checkbox - Only for individual/homepreneur selling products */}
      {isIndividualOrHomepreneur && sellsGoods && (
        <div className="bg-green-50 border border-green-200 p-4 rounded-lg text-left max-w-sm mx-auto">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={gstExempted}
              onChange={(e) => setGstExempted(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-green-300 text-green-600 focus:ring-green-500"
            />
            <div className="flex-1">
              <span className="font-medium text-green-900 text-sm">I only deal in GST-exempted goods</span>
              <p className="mt-1 text-xs text-green-700 leading-relaxed">
                Such as fresh fruits and vegetables, unprocessed milk, unbranded staples, educational books, healthcare items, and raw agricultural products.
              </p>
              <a 
                href="https://cleartax.in/s/gst-exempted-goods-services" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:underline mt-2"
              >
                View comprehensive list of exempted items <ExternalLink size={12} />
              </a>
            </div>
          </label>
        </div>
      )}

      {/* Dynamic Context Message */}
      <div className="bg-gray-50 p-4 rounded-lg text-sm text-left space-y-2 border border-gray-100 max-w-sm mx-auto">
        <p className="font-bold text-gray-700">Next Steps:</p>
        <ul className="space-y-2 text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-blue-500 font-bold">1.</span> 
            <span>Business Verification (Tax & Bank)</span>
          </li>
          {sellsGoods ? (
             <li className="flex items-start gap-2">
               <span className="text-blue-500 font-bold">2.</span> 
               <span>Shipping & Pickup Configuration</span>
             </li>
          ) : (
             <li className="flex items-start gap-2">
               <span className="text-blue-500 font-bold">2.</span> 
               <span>Service Catalog Setup (Shipping Skipped)</span>
             </li>
          )}
          <li className="flex items-start gap-2">
            <span className="text-blue-500 font-bold">3.</span> 
            <span>Select Subscription Plan</span>
          </li>
        </ul>
      </div>

      <button 
        onClick={handleComplete}
        className="w-full max-w-sm bg-indigo-600 text-white py-3 rounded-lg font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all flex items-center justify-center gap-2 mx-auto"
      >
        Proceed to Registration <ChevronRight size={18} />
      </button>
      
      {isNewbie && (
        <p className="text-xs text-gray-400 mt-4">
          Need help? You can request a callback in the Help section later.
        </p>
      )}
    </div>
  );
};

// --- Main Wizard Component ---

const IntentWizard = ({ onComplete, initialData = {}, isInternational = false }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    persona: initialData.persona || null,
    intents: initialData.intents || [],
    context: initialData.context || null,
    online_history: initialData.online_history || null,
    has_photos: initialData.has_photos ?? null,
    has_menu: initialData.has_menu ?? null
  });

  const updateData = (newData) => setFormData({ ...formData, ...newData });

  const handleComplete = () => {
    if (onComplete) {
      onComplete(formData);
    }
  };

  // International flow: Persona (1) → Intent (2) → Transition (3)
  // Standard flow: Persona (1) → Intent (2) → Context (3) → Online History (4) → Transition (5)
  const totalSteps = isInternational ? 3 : 4;

  return (
    <div className="w-full">
      {/* Progress Bar */}
      {step <= totalSteps && (
        <div className="h-1 bg-gray-100 w-full mb-6 rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-600 transition-all duration-500" 
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      )}

      {/* Dynamic Step Rendering */}
      {step === 1 && (
        <StepPersona 
          data={formData} 
          updateData={updateData} 
          onNext={() => setStep(2)} 
        />
      )}
      
      {step === 2 && (
        <StepIntent 
          data={formData} 
          updateData={updateData} 
          onNext={() => setStep(3)}
          onBack={() => setStep(1)}
        />
      )}

      {/* Standard flow: Context and Online History */}
      {!isInternational && step === 3 && (
        <StepContext 
          data={formData} 
          updateData={updateData} 
          onNext={() => setStep(4)}
          onBack={() => setStep(2)}
        />
      )}

      {!isInternational && step === 4 && (
        <StepOnlineHistory 
          data={formData} 
          updateData={updateData} 
          onNext={() => setStep(5)}
          onBack={() => setStep(3)}
        />
      )}

      {/* Transition step: step 3 for international, step 5 for standard */}
      {((isInternational && step === 3) || (!isInternational && step === 5)) && (
        <StepTransition 
          data={formData} 
          onComplete={handleComplete}
        />
      )}
    </div>
  );
};

export default IntentWizard;
